import { useCallback, useState, useRef, MutableRefObject } from 'react';

interface UsePolygonDrawingProps {
  mapRef: MutableRefObject<google.maps.Map | null>;
  isDrawing: boolean;
  isDrawMode: boolean;
}

interface UsePolygonDrawingReturn {
  isPointNearFirstPoint: (
    lat: number,
    lng: number,
    points: Array<[number, number]>
  ) => boolean;
  handleDrawingCursorChange: (
    lat: number,
    lng: number,
    points: Array<[number, number]>
  ) => void;
}

export const usePolygonDrawing = ({
  mapRef,
  isDrawing,
  isDrawMode
}: UsePolygonDrawingProps): UsePolygonDrawingReturn => {
  const isPointNearFirstPoint = useCallback(
    (lat: number, lng: number, points: Array<[number, number]>): boolean => {
      if (points.length < 3) return false;

      const firstPoint = points[0];
      const map = mapRef.current;
      if (!map) return false;

      // Convert lat/lng to pixel coordinates
      const projection = map.getProjection();
      if (!projection) return false;

      const scale = Math.pow(2, map.getZoom() || 0);
      const worldCoordinateFirst = projection.fromLatLngToPoint(
        new google.maps.LatLng(firstPoint[0], firstPoint[1])
      );
      const worldCoordinateCurrent = projection.fromLatLngToPoint(
        new google.maps.LatLng(lat, lng)
      );

      if (!worldCoordinateFirst || !worldCoordinateCurrent) return false;

      const pixelCoordinateFirst = new google.maps.Point(
        worldCoordinateFirst.x * scale,
        worldCoordinateFirst.y * scale
      );
      const pixelCoordinateCurrent = new google.maps.Point(
        worldCoordinateCurrent.x * scale,
        worldCoordinateCurrent.y * scale
      );

      const distance = Math.sqrt(
        Math.pow(pixelCoordinateFirst.x - pixelCoordinateCurrent.x, 2) +
          Math.pow(pixelCoordinateFirst.y - pixelCoordinateCurrent.y, 2)
      );

      return distance < 15;
    },
    [mapRef]
  );

  const handleDrawingCursorChange = useCallback(
    (lat: number, lng: number, points: Array<[number, number]>) => {
      if (!isDrawing || !isDrawMode) return;

      const nearFirstPoint = isPointNearFirstPoint(lat, lng, points);
      if (mapRef.current) {
        mapRef.current.setOptions({
          draggableCursor: nearFirstPoint ? 'pointer' : 'crosshair',
          draggingCursor: nearFirstPoint ? 'pointer' : 'crosshair'
        });
      }
    },
    [isDrawing, isDrawMode, isPointNearFirstPoint, mapRef]
  );

  return {
    isPointNearFirstPoint,
    handleDrawingCursorChange
  };
};
